import { formatPaise } from '@oneapp/gst';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useShortDate } from '../business/parts';
import { Badge, Button, Callout } from '../components/ui';
import { salesApi, salesKeys } from './api';

const KIND_TONE = { b2b: 'navy', b2cl: 'saffron', b2cs: 'gray' } as const;

/**
 * K8 · Correct a sale (gap 4b): the month's GSTR-1 as filed, to pick the sale GSTR-1A corrects. Sales corrected
 * already say so (picking one changes its correction). Walk-in totals and notes aren't corrected here.
 */
export function CorrectList({
  gstr1ReturnId,
  periodName,
  corrected,
  onPick,
}: {
  /** The month's GSTR-1, filed through EkApp. */
  gstr1ReturnId: string;
  /** "October". */
  periodName: string;
  /** The filed sales corrected so far. */
  corrected: ReadonlySet<string>;
  onPick: (invoiceId: string) => void;
}) {
  const { t } = useTranslation();
  const date = useShortDate();
  const list = useInfiniteQuery({
    queryKey: salesKeys.invoices(gstr1ReturnId, 'all'),
    queryFn: ({ pageParam, signal }) => salesApi.invoices(gstr1ReturnId, 'all', pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const sales = (list.data?.pages.flatMap((page) => page.invoices) ?? []).filter(
    (invoice) => invoice.docType === 'invoice' && invoice.source !== 'walk_in',
  );
  return (
    <div className="sales">
      <div className="sales__head">
        <h2 className="sheet__title">{t('sales.correct.pickTitle', { month: periodName })}</h2>
        <p className="sheet__text muted">{t('sales.correct.pickLead')}</p>
      </div>
      {list.isPending ? (
        <p className="muted">{t('sales.list.loading')}</p>
      ) : list.isError ? (
        <Callout tone="error" icon="alert-triangle">
          {t('sales.list.loadFailed')}
        </Callout>
      ) : sales.length ? (
        <ul className="sales__list">
          {sales.map((invoice) => (
            <li key={invoice.id}>
              <button type="button" className="sales__row" onClick={() => onPick(invoice.id)}>
                <span className="sales__text">
                  <span className="sales__number">{invoice.numbers[0]}</span>
                  <span className="sales__meta">
                    {invoice.buyer ? (invoice.buyer.name ?? invoice.buyer.gstin) : t('sales.list.consumer')} ·{' '}
                    {date(invoice.date)}
                  </span>
                </span>
                <span className="sales__amount">
                  <span>{formatPaise(invoice.taxableValue)}</span>
                  {corrected.has(invoice.id) ? (
                    <Badge tone="amber">{t('sales.correct.corrected')}</Badge>
                  ) : (
                    <Badge tone={KIND_TONE[invoice.kind]}>{t(`sales.list.kind.${invoice.kind}`)}</Badge>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">{t('sales.correct.none')}</p>
      )}
      {list.hasNextPage ? (
        <Button
          variant="secondary"
          small
          inline
          disabled={list.isFetchingNextPage}
          onClick={() => list.fetchNextPage()}
        >
          {list.isFetchingNextPage ? t('sales.list.loadingMore') : t('sales.list.loadMore')}
        </Button>
      ) : null}
    </div>
  );
}
